import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { getCrmApiBaseUrl } from "../../utils/utils";

export default function LiveScanMonitor() {
  const BASE = getCrmApiBaseUrl().replace(/\/+$/, "") + "/v2.0/ekcargo/emirates-scans";

  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [count, setCount] = useState(0);

  const [filterAwb, setFilterAwb] = useState("");
  const [filterRetryStatus, setFilterRetryStatus] = useState("");
  const [filterOk, setFilterOk] = useState("");
  const [filterSource, setFilterSource] = useState("");
  const [limit, setLimit] = useState(100);
  const [showFilters, setShowFilters] = useState(true);

  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(5);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [countdown, setCountdown] = useState(0);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [retrying, setRetrying] = useState(false);
  const [detail, setDetail] = useState(null);

  const timerRef = useRef(null);
  const countdownRef = useRef(null);
  const countdownValRef = useRef(0);

  const buildParams = useCallback(() => {
    const p = new URLSearchParams();
    p.append("limit", String(limit));
    if (filterAwb) p.append("awb", filterAwb);
    if (filterRetryStatus) p.append("retryStatus", filterRetryStatus);
    if (filterOk !== "") p.append("ok", filterOk);
    if (filterSource) p.append("source", filterSource);
    return p;
  }, [filterAwb, filterRetryStatus, filterOk, filterSource, limit]);

  const refresh = useCallback(async (showSpinner = false) => {
    if (showSpinner) setLoading(true);
    setError("");
    try {
      // Fetch logs + stats in parallel; stats failure is non-fatal
      const [logsRes, statsRes] = await Promise.allSettled([
        axios.get(`${BASE}/logs?${buildParams().toString()}`),
        axios.get(`${BASE}/stats`),
      ]);

      const logsData = logsRes.status === "fulfilled" ? (logsRes.value.data || {}) : {};
      if (logsRes.status === "rejected") {
        throw logsRes.reason;
      }
      const loadedLogs = Array.isArray(logsData.logs) ? logsData.logs : [];
      setLogs(loadedLogs);
      setCount(logsData.count ?? loadedLogs.length);

      // Use stats API response if it has meaningful data, otherwise compute from loaded logs
      const apiStats = statsRes.status === "fulfilled" ? (statsRes.value.data || {}) : {};
      const apiTotal = apiStats.total ?? apiStats.count ?? null;
      if (apiTotal != null && apiTotal > 0) {
        setStats(apiStats);
      } else {
        // Compute stats from the currently loaded logs as reliable fallback
        const computed = { total: logsData.count ?? loadedLogs.length, byOk: {}, byRetryStatus: {} };
        loadedLogs.forEach((l) => {
          const okKey = String(l.ok);
          computed.byOk[okKey] = (computed.byOk[okKey] || 0) + 1;
          const rs = l.retryStatus || l.retry_status || "NONE";
          computed.byRetryStatus[rs] = (computed.byRetryStatus[rs] || 0) + 1;
        });
        setStats(computed);
      }

      setLastRefreshed(new Date());
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Failed to load scan logs");
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, [BASE, buildParams]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(true); }, [filterAwb, filterRetryStatus, filterOk, filterSource, limit]);

  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    timerRef.current = null;
    countdownRef.current = null;

    if (!autoRefresh) { setCountdown(0); return; }

    const interval = Math.max(2, refreshInterval);
    countdownValRef.current = interval;
    setCountdown(interval);

    countdownRef.current = setInterval(() => {
      countdownValRef.current -= 1;
      setCountdown(countdownValRef.current);
      if (countdownValRef.current <= 0) {
        countdownValRef.current = interval;
        setCountdown(interval);
      }
    }, 1000);

    timerRef.current = setInterval(() => refresh(false), interval * 1000);

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [autoRefresh, refreshInterval, refresh]);

  const toggleSelect = (id) => {
    setSelectedIds((cur) => {
      const next = new Set(cur);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const retryOne = async (id) => {
    setRetrying(true); setError(""); setSuccess("");
    try {
      const r = await axios.post(`${BASE}/retry/${id}`);
      setSuccess(r.data?.note || `Retry queued for #${id}`);
      await refresh(false);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Retry failed");
    } finally { setRetrying(false); }
  };

  const retryBulk = async () => {
    if (selectedIds.size === 0) { setError("Select at least one row to retry"); return; }
    setRetrying(true); setError(""); setSuccess("");
    try {
      const r = await axios.post(`${BASE}/retry`, { ids: Array.from(selectedIds), additionalAttempts: 3 });
      const d = r.data || {};
      setSuccess(`Queued ${d.queuedCount ?? 0} retry/retries${d.notFound?.length ? `, ${d.notFound.length} not found` : ""}.`);
      setSelectedIds(new Set());
      await refresh(false);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Bulk retry failed");
    } finally { setRetrying(false); }
  };

  const clearFilters = () => {
    setFilterAwb(""); setFilterRetryStatus(""); setFilterOk(""); setFilterSource("");
  };

  const activeFilterCount = [filterAwb, filterRetryStatus, filterOk, filterSource].filter(Boolean).length;

  const okBadge = (ok) => {
    const cfg = ok
      ? { bg: "#dcfce7", color: "#166534", txt: "OK", icon: "fa-check-circle" }
      : { bg: "#fee2e2", color: "#991b1b", txt: "FAIL", icon: "fa-times-circle" };
    return (
      <span style={{ ...s.badge, background: cfg.bg, color: cfg.color }}>
        <i className={`fa ${cfg.icon}`} style={{ marginRight: 3, fontSize: 10 }} />{cfg.txt}
      </span>
    );
  };

  const retryBadge = (rs) => {
    const map = {
      NONE: { bg: "#f3f4f6", color: "#374151" },
      PENDING: { bg: "#fef3c7", color: "#92400e" },
      INFLIGHT: { bg: "#dbeafe", color: "#1e40af" },
      DONE: { bg: "#dcfce7", color: "#166534" },
      EXHAUSTED: { bg: "#fee2e2", color: "#991b1b" },
    };
    const cfg = map[rs] || map.NONE;
    return <span style={{ ...s.badge, background: cfg.bg, color: cfg.color }}>{rs || "—"}</span>;
  };

  const allChecked = logs.length > 0 && logs.every((l) => selectedIds.has(l.id));

  return (
    <>
      <style>{`
        .lsm-row:hover td { background: #f8fafc; }
        .lsm-row.lsm-selected td { background: #eff6ff; }
        .lsm-btn-icon:hover { background: #f1f5f9 !important; border-color: #7c3aed !important; }
        .lsm-btn-icon:hover i { color: #7c3aed !important; }
        .lsm-stat-card { transition: transform 0.15s ease, box-shadow 0.15s ease; cursor: default; }
        .lsm-stat-card:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(0,0,0,0.08); }
        .lsm-input:focus { border-color: #7c3aed !important; box-shadow: 0 0 0 3px rgba(124,58,237,0.1) !important; outline: none; }
        .lsm-select:focus { border-color: #7c3aed !important; box-shadow: 0 0 0 3px rgba(124,58,237,0.1) !important; outline: none; }
        .lsm-filter-toggle:hover { background: #f8fafc; }
        @keyframes lsm-spin { to { transform: rotate(360deg); } }
        @keyframes lsm-pulse-dot { 0%,100% { opacity:1; transform:scale(1); } 50% { opacity:0.6; transform:scale(0.85); } }
        @keyframes lsm-fadein { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
        @keyframes lsm-slide-down { from { opacity:0; max-height:0; } to { opacity:1; max-height:400px; } }
        .lsm-spinner { animation: lsm-spin 0.7s linear infinite; }
        .lsm-live-dot { animation: lsm-pulse-dot 1.8s ease-in-out infinite; }
        .lsm-modal-in { animation: lsm-fadein 0.2s ease; }
        .lsm-filters-body { animation: lsm-slide-down 0.2s ease; }
        @media (max-width: 900px) {
          .lsm-header { flex-direction: column !important; align-items: flex-start !important; }
          .lsm-header-right { width: 100%; }
          .lsm-stats-grid { grid-template-columns: repeat(3, 1fr) !important; }
          .lsm-filter-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 600px) {
          .lsm-page { padding: 12px 14px !important; }
          .lsm-stats-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .lsm-filter-grid { grid-template-columns: 1fr !important; }
          .lsm-toolbar { flex-direction: column; align-items: flex-start !important; }
          .lsm-modal-content { border-radius: 12px !important; max-height: 95vh !important; }
          .lsm-detail-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>

      <div className="lsm-page" style={s.page}>

        {/* ── Header ───────────────────────────────── */}
        <div className="lsm-header" style={s.header}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={s.headerIconBox}>
              <i className="fa fa-broadcast-tower" style={{ fontSize: 22, color: "#7c3aed" }} />
            </div>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <h1 style={s.pageTitle}>Live Scan Monitor</h1>
                {autoRefresh && <span className="lsm-live-dot" style={s.liveDot} title="Auto-refreshing" />}
              </div>
              <p style={s.pageSubtitle}>
                Emirates scan feed &middot; <code style={s.inlineCode}>{getCrmApiBaseUrl()}</code>
                <code style={{ ...s.inlineCode, marginLeft: 4 }}>/v2.0/ekcargo/emirates-scans</code>
              </p>
            </div>
          </div>

          <div className="lsm-header-right" style={s.headerRight}>
            {lastRefreshed && (
              <span style={s.lastRefreshedChip}>
                <i className="fa fa-clock-o" style={{ marginRight: 4 }} />
                {lastRefreshed.toLocaleTimeString()}
              </span>
            )}

            <div style={s.refreshGroup}>
              <label style={s.toggleWrap} onClick={() => setAutoRefresh(v => !v)}>
                <div style={{ ...s.toggleTrack, background: autoRefresh ? "#7c3aed" : "#cbd5e1" }}>
                  <div style={{ ...s.toggleThumb, transform: autoRefresh ? "translateX(18px)" : "translateX(2px)" }} />
                </div>
                <span style={{ fontSize: 12, color: "#475569", fontWeight: 600 }}>Auto</span>
              </label>

              {autoRefresh && (
                <>
                  <select
                    className="lsm-select"
                    value={refreshInterval}
                    onChange={(e) => setRefreshInterval(Number(e.target.value))}
                    style={s.selectSm}
                  >
                    <option value={2}>2s</option>
                    <option value={5}>5s</option>
                    <option value={10}>10s</option>
                    <option value={30}>30s</option>
                    <option value={60}>60s</option>
                  </select>
                  <span style={s.countdownPill} title="Next refresh in">{countdown}s</span>
                </>
              )}
            </div>

            <button onClick={() => refresh(true)} style={s.btnPrimary} disabled={loading}>
              {loading
                ? <span className="lsm-spinner" style={s.spinnerSm} />
                : <i className="fa fa-refresh" />}
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {/* ── Alerts ───────────────────────────────── */}
        {error && (
          <div style={s.alertError}>
            <i className="fa fa-exclamation-circle" style={{ flexShrink: 0 }} />
            <span style={{ flex: 1 }}>{error}</span>
            <button onClick={() => setError("")} style={s.alertDismiss}>×</button>
          </div>
        )}
        {success && (
          <div style={s.alertSuccess}>
            <i className="fa fa-check-circle" style={{ flexShrink: 0 }} />
            <span style={{ flex: 1 }}>{success}</span>
            <button onClick={() => setSuccess("")} style={s.alertDismiss}>×</button>
          </div>
        )}

        {/* ── Stats ────────────────────────────────── */}
        {stats && (
          <div className="lsm-stats-grid" style={s.statsGrid}>
            <StatCard icon="fa-list-ul" label="Total scans" value={stats.total ?? "—"} color="#7c3aed" bg="#f5f3ff" />
            <StatCard icon="fa-check-circle" label="Successful" value={stats.byOk?.true ?? stats.byOk?.["true"] ?? 0} color="#16a34a" bg="#f0fdf4" />
            <StatCard icon="fa-times-circle" label="Failed" value={stats.byOk?.false ?? stats.byOk?.["false"] ?? 0} color="#dc2626" bg="#fef2f2" />
            <StatCard icon="fa-clock-o" label="Pending retry" value={stats.byRetryStatus?.PENDING ?? 0} color="#d97706" bg="#fffbeb" />
            <StatCard icon="fa-spinner" label="In-flight" value={stats.byRetryStatus?.INFLIGHT ?? 0} color="#2563eb" bg="#eff6ff" />
            <StatCard icon="fa-ban" label="Exhausted" value={stats.byRetryStatus?.EXHAUSTED ?? 0} color="#991b1b" bg="#fef2f2" />
          </div>
        )}

        {/* ── Filters ──────────────────────────────── */}
        <div style={s.card}>
          <button
            className="lsm-filter-toggle"
            onClick={() => setShowFilters(v => !v)}
            style={s.filterToggleBtn}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <i className="fa fa-sliders" style={{ color: "#7c3aed", fontSize: 14 }} />
              <span style={s.sectionTitle}>Filters</span>
              {activeFilterCount > 0 && (
                <span style={s.activeBadge}>{activeFilterCount} active</span>
              )}
            </div>
            <i
              className={`fa fa-chevron-${showFilters ? "up" : "down"}`}
              style={{ color: "#94a3b8", fontSize: 11 }}
            />
          </button>

          {showFilters && (
            <div className="lsm-filters-body" style={{ padding: "16px 18px 18px" }}>
              <div className="lsm-filter-grid" style={s.filterGrid}>
                <div>
                  <label style={s.filterLabel}>AWB contains</label>
                  <div style={{ position: "relative" }}>
                    <i className="fa fa-search" style={s.inputPrefixIcon} />
                    <input
                      className="lsm-input"
                      value={filterAwb}
                      onChange={(e) => setFilterAwb(e.target.value)}
                      placeholder="e.g. 310100…"
                      style={{ ...s.input, paddingLeft: 32 }}
                    />
                  </div>
                </div>
                <div>
                  <label style={s.filterLabel}>Retry status</label>
                  <select className="lsm-select" value={filterRetryStatus} onChange={(e) => setFilterRetryStatus(e.target.value)} style={s.select}>
                    <option value="">All statuses</option>
                    <option value="NONE">NONE</option>
                    <option value="PENDING">PENDING</option>
                    <option value="INFLIGHT">INFLIGHT</option>
                    <option value="DONE">DONE</option>
                    <option value="EXHAUSTED">EXHAUSTED</option>
                  </select>
                </div>
                <div>
                  <label style={s.filterLabel}>Result</label>
                  <select className="lsm-select" value={filterOk} onChange={(e) => setFilterOk(e.target.value)} style={s.select}>
                    <option value="">All results</option>
                    <option value="true">Successful only</option>
                    <option value="false">Failed only</option>
                  </select>
                </div>
                <div>
                  <label style={s.filterLabel}>Source</label>
                  <div style={{ position: "relative" }}>
                    <i className="fa fa-tag" style={s.inputPrefixIcon} />
                    <input
                      className="lsm-input"
                      value={filterSource}
                      onChange={(e) => setFilterSource(e.target.value)}
                      placeholder="e.g. EKCARGO"
                      style={{ ...s.input, paddingLeft: 32 }}
                    />
                  </div>
                </div>
                <div>
                  <label style={s.filterLabel}>Row limit</label>
                  <select className="lsm-select" value={limit} onChange={(e) => setLimit(Number(e.target.value))} style={s.select}>
                    <option value={50}>50 rows</option>
                    <option value={100}>100 rows</option>
                    <option value={200}>200 rows</option>
                    <option value={500}>500 rows</option>
                    <option value={1000}>1000 rows</option>
                  </select>
                </div>
                <div style={{ display: "flex", alignItems: "flex-end" }}>
                  <button
                    onClick={clearFilters}
                    disabled={activeFilterCount === 0}
                    style={{
                      ...s.btnSecondary,
                      opacity: activeFilterCount === 0 ? 0.45 : 1,
                      cursor: activeFilterCount === 0 ? "not-allowed" : "pointer",
                      width: "100%",
                      justifyContent: "center",
                    }}
                  >
                    <i className="fa fa-times" />Clear filters
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Table card ───────────────────────────── */}
        <div style={s.card}>
          <div className="lsm-toolbar" style={s.tableToolbar}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={s.sectionTitle}>Scan Logs</span>
              <span style={s.countPill}>
                {count.toLocaleString()}{logs.length !== count ? ` · showing ${logs.length}` : ""}
              </span>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              {selectedIds.size > 0 && (
                <span style={{ fontSize: 12, color: "#64748b" }}>{selectedIds.size} selected</span>
              )}
              <button
                onClick={retryBulk}
                disabled={selectedIds.size === 0 || retrying}
                style={{
                  ...s.btnPrimary,
                  background: selectedIds.size === 0 ? "#e2e8f0" : "#7c3aed",
                  color: selectedIds.size === 0 ? "#94a3b8" : "#fff",
                  cursor: selectedIds.size === 0 ? "not-allowed" : "pointer",
                }}
              >
                <i className="fa fa-repeat" />
                {retrying ? "Retrying…" : `Retry selected${selectedIds.size ? ` (${selectedIds.size})` : ""}`}
              </button>
            </div>
          </div>

          <div style={{ overflowX: "auto" }}>
            {loading ? (
              <div style={s.loadingState}>
                <div className="lsm-spinner" style={s.spinnerLg} />
                <span style={{ marginTop: 14, color: "#94a3b8", fontSize: 13 }}>Loading scan logs…</span>
              </div>
            ) : logs.length === 0 ? (
              <div style={s.emptyState}>
                <i className="fa fa-inbox" style={{ fontSize: 40, color: "#cbd5e1", display: "block", marginBottom: 12 }} />
                <div style={{ fontSize: 15, fontWeight: 700, color: "#64748b" }}>No scan logs found</div>
                <div style={{ fontSize: 13, color: "#94a3b8", marginTop: 4 }}>
                  {activeFilterCount > 0 ? "Try adjusting your filters" : "No data available"}
                </div>
              </div>
            ) : (
              <table style={s.table}>
                <thead>
                  <tr>
                    <th style={{ ...s.th, width: 40, textAlign: "center" }}>
                      <input
                        type="checkbox"
                        checked={allChecked}
                        onChange={(e) => setSelectedIds(e.target.checked ? new Set(logs.map(l => l.id)) : new Set())}
                        style={{ cursor: "pointer" }}
                      />
                    </th>
                    <th style={s.th}>ID</th>
                    <th style={s.th}>Created</th>
                    <th style={s.th}>AWB</th>
                    <th style={s.th}>Source</th>
                    <th style={s.th}>Status</th>
                    <th style={s.th}>Result</th>
                    <th style={s.th}>HTTP</th>
                    <th style={s.th}>Retry</th>
                    <th style={{ ...s.th, textAlign: "center" }}>Attempts</th>
                    <th style={s.th}>Next retry</th>
                    <th style={s.th}>Message</th>
                    <th style={{ ...s.th, textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((l, i) => (
                    <tr
                      key={l.id}
                      className={`lsm-row${selectedIds.has(l.id) ? " lsm-selected" : ""}`}
                      style={{ borderBottom: "1px solid #f1f5f9" }}
                    >
                      <td style={{ ...s.td, textAlign: "center" }}>
                        <input type="checkbox" checked={selectedIds.has(l.id)} onChange={() => toggleSelect(l.id)} style={{ cursor: "pointer" }} />
                      </td>
                      <td style={{ ...s.td, fontFamily: "monospace", fontSize: 11, color: "#94a3b8" }}>{l.id}</td>
                      <td style={{ ...s.td, fontSize: 12, whiteSpace: "nowrap", color: "#64748b" }}>{formatTs(l.createdDate || l.created_date)}</td>
                      <td style={{ ...s.td, fontFamily: "monospace", color: "#2563eb", fontWeight: 700 }}>{l.awb || "—"}</td>
                      <td style={s.td}>
                        {l.source ? <span style={s.sourcePill}>{l.source}</span> : <span style={{ color: "#94a3b8" }}>—</span>}
                      </td>
                      <td style={{ ...s.td, fontSize: 12, color: "#475569" }}>{l.taskStatus || l.task_status || "—"}</td>
                      <td style={s.td}>{okBadge(l.ok)}</td>
                      <td style={{ ...s.td, fontFamily: "monospace", fontWeight: 700, fontSize: 13 }}>
                        <span style={{ color: (l.httpStatus ?? l.http_status) >= 400 ? "#dc2626" : "#16a34a" }}>
                          {l.httpStatus ?? l.http_status ?? "—"}
                        </span>
                      </td>
                      <td style={s.td}>{retryBadge(l.retryStatus || l.retry_status)}</td>
                      <td style={{ ...s.td, textAlign: "center" }}>
                        <span style={{ fontFamily: "monospace", fontSize: 12 }}>
                          {l.retryCount ?? l.retry_count ?? 0}
                          <span style={{ color: "#cbd5e1" }}>/</span>
                          <span style={{ color: "#94a3b8" }}>{l.maxAttempts ?? l.max_attempts ?? "—"}</span>
                        </span>
                      </td>
                      <td style={{ ...s.td, fontSize: 11, color: "#94a3b8", whiteSpace: "nowrap" }}>{formatTs(l.nextRetryAt || l.next_retry_at)}</td>
                      <td
                        style={{ ...s.td, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 12, color: "#64748b" }}
                        title={l.message || l.errorMsg}
                      >
                        {l.message || l.errorMsg || <span style={{ color: "#cbd5e1" }}>—</span>}
                      </td>
                      <td style={{ ...s.td, textAlign: "right", whiteSpace: "nowrap" }}>
                        <button className="lsm-btn-icon" onClick={() => setDetail(l)} style={s.btnIcon} title="View detail">
                          <i className="fa fa-eye" style={{ color: "#64748b" }} />
                        </button>
                        <button className="lsm-btn-icon" onClick={() => retryOne(l.id)} style={{ ...s.btnIcon, marginLeft: 5 }} disabled={retrying} title="Retry this scan">
                          <i className="fa fa-repeat" style={{ color: "#64748b" }} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* ── Detail Modal ─────────────────────────── */}
        {detail && (
          <div onClick={() => setDetail(null)} style={s.modalOverlay}>
            <div
              className="lsm-modal-in lsm-modal-content"
              onClick={(e) => e.stopPropagation()}
              style={s.modalContent}
            >
              {/* Modal header */}
              <div style={s.modalHeader}>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={s.modalHeaderIcon}>
                    <i className="fa fa-file-text-o" style={{ color: "#7c3aed", fontSize: 17 }} />
                  </div>
                  <div>
                    <h3 style={s.modalTitle}>Scan #{detail.id}</h3>
                    <p style={s.modalSubtitle}>
                      AWB: <strong style={{ color: "#2563eb", fontFamily: "monospace" }}>{detail.awb || "—"}</strong>
                      {detail.source && <span style={{ ...s.sourcePill, marginLeft: 8 }}>{detail.source}</span>}
                    </p>
                  </div>
                </div>
                <button onClick={() => setDetail(null)} style={s.modalCloseBtn} title="Close">
                  <i className="fa fa-times" />
                </button>
              </div>

              {/* Modal body */}
              <div style={s.modalBody}>
                <div className="lsm-detail-grid" style={s.detailGrid}>
                  <DetailField label="Created" value={formatTs(detail.createdDate || detail.created_date)} />
                  <DetailField label="Last attempt" value={formatTs(detail.lastAttemptAt || detail.last_attempt_at)} />
                  <DetailField label="Task status" value={detail.taskStatus || detail.task_status} />
                  <DetailField label="HTTP status" value={detail.httpStatus ?? detail.http_status} highlight={(v) => v >= 400 ? "#fef2f2" : "#f0fdf4"} />
                  <DetailField label="Retry status" value={detail.retryStatus || detail.retry_status} />
                  <DetailField label="Attempts" value={`${detail.retryCount ?? detail.retry_count ?? 0} / ${detail.maxAttempts ?? detail.max_attempts ?? "—"}`} />
                  <DetailField label="Piece reference" value={detail.pieceReference || detail.piece_reference} mono />
                  <DetailField label="Event code" value={detail.emiratesEventCode || detail.emirates_event_code} />
                  <DetailField label="Exception code" value={detail.emiratesExceptionCode || detail.emirates_exception_code} />
                  <DetailField label="Last error code" value={detail.lastErrorCode || detail.last_error_code} />
                  <DetailField label="Dedup key" value={detail.dedupKey || detail.dedup_key} mono />
                  <DetailField label="Next retry at" value={formatTs(detail.nextRetryAt || detail.next_retry_at)} />
                </div>

                {(detail.message || detail.errorMsg || detail.error_msg) && (
                  <PreBlock label="Message / Error" value={detail.message || detail.errorMsg || detail.error_msg} />
                )}
                {(detail.requestPayload || detail.request_payload) && (
                  <PreBlock label="Request Payload" value={detail.requestPayload || detail.request_payload} />
                )}
                {(detail.responseBody || detail.response_body) && (
                  <PreBlock label="Response Body" value={detail.responseBody || detail.response_body} />
                )}
              </div>

              {/* Modal footer */}
              <div style={s.modalFooter}>
                <button
                  onClick={() => { retryOne(detail.id); setDetail(null); }}
                  style={s.btnPrimary}
                  disabled={retrying}
                >
                  <i className="fa fa-repeat" />Retry this scan
                </button>
                <button onClick={() => setDetail(null)} style={s.btnSecondary}>Close</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// ── Sub-components ──────────────────────────────────────

const StatCard = ({ icon, label, value, color, bg }) => (
  <div className="lsm-stat-card" style={{
    background: "#fff",
    border: "1px solid #e2e8f0",
    borderRadius: 12,
    padding: "16px 18px",
    display: "flex",
    alignItems: "center",
    gap: 14,
  }}>
    <div style={{
      width: 44, height: 44, borderRadius: 10, background: bg,
      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
    }}>
      <i className={`fa ${icon}`} style={{ color, fontSize: 19 }} />
    </div>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 26, fontWeight: 800, color: "#0f172a", lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5, marginTop: 4 }}>{label}</div>
    </div>
  </div>
);

const DetailField = ({ label, value, mono, highlight }) => {
  const bg = highlight && value != null ? highlight(value) : "#f8fafc";
  return (
    <div style={{ background: bg, borderRadius: 8, padding: "10px 12px" }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 13, color: "#0f172a", fontFamily: mono ? "monospace" : "inherit", wordBreak: "break-all" }}>{value ?? <span style={{ color: "#94a3b8" }}>—</span>}</div>
    </div>
  );
};

const PreBlock = ({ label, value }) => (
  <div style={{ marginTop: 14 }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>{label}</div>
    <pre style={{
      background: "#0f172a", color: "#e2e8f0", padding: "12px 16px", borderRadius: 8,
      fontSize: 12, fontFamily: "monospace", overflowX: "auto", whiteSpace: "pre-wrap",
      wordBreak: "break-all", maxHeight: 220, margin: 0, lineHeight: 1.6,
    }}>{value}</pre>
  </div>
);

const formatTs = (v) => {
  if (!v) return "—";
  try {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return String(v);
    return d.toLocaleString();
  } catch { return String(v); }
};

// ── Styles ──────────────────────────────────────────────

const s = {
  page: { padding: "22px 28px", background: "#f8fafc", minHeight: "100vh", fontFamily: "inherit" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 14, marginBottom: 20 },
  headerIconBox: { width: 52, height: 52, borderRadius: 14, background: "#f5f3ff", border: "1px solid #ede9fe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  pageTitle: { fontSize: 21, fontWeight: 800, color: "#0f172a", margin: 0, letterSpacing: -0.3 },
  pageSubtitle: { fontSize: 12, color: "#94a3b8", margin: "4px 0 0" },
  inlineCode: { background: "#f1f5f9", padding: "1px 6px", borderRadius: 4, fontSize: 11, color: "#475569", fontFamily: "monospace" },
  liveDot: { width: 9, height: 9, borderRadius: "50%", background: "#22c55e", display: "inline-block", boxShadow: "0 0 0 3px rgba(34,197,94,0.2)" },
  headerRight: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
  lastRefreshedChip: { fontSize: 12, color: "#94a3b8", background: "#f1f5f9", padding: "4px 10px", borderRadius: 20, whiteSpace: "nowrap" },
  refreshGroup: { display: "flex", alignItems: "center", gap: 8, background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "5px 10px" },
  toggleWrap: { display: "flex", alignItems: "center", gap: 7, cursor: "pointer", userSelect: "none" },
  toggleTrack: { width: 36, height: 20, borderRadius: 10, position: "relative", transition: "background 0.2s", flexShrink: 0 },
  toggleThumb: { position: "absolute", top: 2, width: 16, height: 16, borderRadius: "50%", background: "#fff", transition: "transform 0.2s", boxShadow: "0 1px 4px rgba(0,0,0,0.25)" },
  selectSm: { padding: "4px 8px", border: "1px solid #e2e8f0", borderRadius: 6, fontSize: 12, background: "#fff", color: "#374151" },
  countdownPill: { background: "#7c3aed", color: "#fff", borderRadius: 20, padding: "2px 9px", fontSize: 11, fontWeight: 700, minWidth: 32, textAlign: "center" },
  btnPrimary: { display: "inline-flex", alignItems: "center", gap: 7, padding: "8px 16px", borderRadius: 8, border: "none", background: "#7c3aed", color: "#fff", fontWeight: 700, cursor: "pointer", fontSize: 13, transition: "opacity 0.15s", whiteSpace: "nowrap" },
  btnSecondary: { display: "inline-flex", alignItems: "center", gap: 7, padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: "#fff", color: "#374151", fontWeight: 600, cursor: "pointer", fontSize: 13, whiteSpace: "nowrap" },
  spinnerSm: { width: 14, height: 14, border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "#fff", borderRadius: "50%", display: "inline-block" },
  alertError: { background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", padding: "10px 14px", borderRadius: 9, marginBottom: 12, fontSize: 13, display: "flex", alignItems: "center", gap: 8 },
  alertSuccess: { background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#166534", padding: "10px 14px", borderRadius: 9, marginBottom: 12, fontSize: 13, display: "flex", alignItems: "center", gap: 8 },
  alertDismiss: { marginLeft: "auto", background: "none", border: "none", cursor: "pointer", fontSize: 20, color: "inherit", lineHeight: 1, padding: "0 2px" },
  statsGrid: { display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 10, marginBottom: 16 },
  card: { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, marginBottom: 14, overflow: "hidden", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" },
  sectionTitle: { fontSize: 14, fontWeight: 700, color: "#0f172a" },
  filterToggleBtn: { width: "100%", background: "none", border: "none", cursor: "pointer", padding: "13px 18px", display: "flex", justifyContent: "space-between", alignItems: "center", transition: "background 0.1s" },
  activeBadge: { background: "#7c3aed", color: "#fff", borderRadius: 20, padding: "2px 8px", fontSize: 11, fontWeight: 700 },
  filterGrid: { display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 12 },
  filterLabel: { display: "block", fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 5 },
  inputPrefixIcon: { position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", fontSize: 12, pointerEvents: "none" },
  input: { width: "100%", padding: "8px 11px", border: "1px solid #e2e8f0", borderRadius: 7, fontSize: 13, fontFamily: "inherit", color: "#374151", background: "#fff", transition: "border-color 0.15s, box-shadow 0.15s", boxSizing: "border-box" },
  select: { width: "100%", padding: "8px 11px", border: "1px solid #e2e8f0", borderRadius: 7, fontSize: 13, background: "#fff", color: "#374151", transition: "border-color 0.15s, box-shadow 0.15s" },
  tableToolbar: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "13px 18px", borderBottom: "1px solid #f1f5f9", flexWrap: "wrap", gap: 8 },
  countPill: { background: "#f1f5f9", color: "#475569", borderRadius: 20, padding: "2px 10px", fontSize: 12, fontWeight: 700 },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: { padding: "10px 12px", textAlign: "left", fontWeight: 700, color: "#64748b", fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4, background: "#f8fafc", borderBottom: "2px solid #e2e8f0", whiteSpace: "nowrap" },
  td: { padding: "9px 12px", color: "#1e293b", verticalAlign: "middle" },
  badge: { display: "inline-flex", alignItems: "center", padding: "3px 8px", borderRadius: 999, fontSize: 11, fontWeight: 700 },
  sourcePill: { background: "#ede9fe", color: "#6d28d9", borderRadius: 5, padding: "2px 7px", fontSize: 11, fontWeight: 700 },
  btnIcon: { width: 30, height: 30, borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", transition: "all 0.1s" },
  loadingState: { display: "flex", flexDirection: "column", alignItems: "center", padding: "64px 40px" },
  spinnerLg: { width: 38, height: 38, border: "3px solid #f1f5f9", borderTopColor: "#7c3aed", borderRadius: "50%" },
  emptyState: { padding: "56px 40px", textAlign: "center" },
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(15,23,42,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 20, backdropFilter: "blur(3px)" },
  modalContent: { background: "#fff", borderRadius: 16, maxWidth: 880, width: "100%", maxHeight: "90vh", overflow: "auto", boxShadow: "0 32px 80px rgba(0,0,0,0.22)" },
  modalHeader: { padding: "20px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "flex-start", position: "sticky", top: 0, background: "#fff", zIndex: 1 },
  modalHeaderIcon: { width: 42, height: 42, borderRadius: 10, background: "#f5f3ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  modalTitle: { margin: 0, fontSize: 17, fontWeight: 800, color: "#0f172a" },
  modalSubtitle: { margin: "3px 0 0", fontSize: 12, color: "#64748b" },
  modalCloseBtn: { width: 32, height: 32, borderRadius: 8, border: "1px solid #e2e8f0", background: "#f8fafc", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#64748b", fontSize: 13, flexShrink: 0 },
  modalBody: { padding: "20px 24px" },
  detailGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 4 },
  modalFooter: { padding: "16px 24px", borderTop: "1px solid #f1f5f9", display: "flex", gap: 10, background: "#f8fafc", flexWrap: "wrap" },
};
